import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, Copy, Link2 } from "lucide-react";
import { fetchPartnerDashboard, type PartnerDashboardCode } from "../api/partners";
import { AtlasPanel } from "../components/AtlasPanel";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { Skel } from "../components/Skeleton";
import { StatBlock } from "../components/StatBlock";
import { usePageMeta } from "../hooks/usePageMeta";

// Partner dashboard: a creator's own /with/{code} link and the signups it
// brought in. Counts only: the API never says who signed up. Linked from the
// sidebar only for accounts that own a code (set in /admin/partners).

export const PARTNER_DASHBOARD_KEY = ["partner"] as const;

const weekFormat = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" });
const dateFormat = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" });

function formatWeek(weekStart: string): string {
  return `Week of ${weekFormat.format(new Date(`${weekStart}T12:00:00`))}`;
}

function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked: the link is on screen to copy by hand.
    }
  };
  return (
    <Button variant="primary" size="sm" leftIcon={copied ? <Check size={14} /> : <Copy size={14} />} onClick={copy}>
      {copied ? "Copied" : "Copy link"}
    </Button>
  );
}

function CodePanel({ code }: { code: PartnerDashboardCode }) {
  const url = `${window.location.origin}/with/${code.code}`;
  const { signedUp, sourceConnected, activated } = code.totals;
  const weeksWithActivity = code.weeks.filter((w) => w.signedUp > 0 || w.activated > 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <AtlasPanel title="Your link">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div style={{ minWidth: 0 }}>
            <p
              className="tnum"
              style={{ fontFamily: "var(--font-mono)", fontSize: 15, color: "var(--text-primary)", margin: 0, wordBreak: "break-all" }}
            >
              {url}
            </p>
            <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "4px 0 0" }}>
              Live since {dateFormat.format(new Date(code.createdAt))}. People who open it and create their account
              from it count here. Someone who skips the link and installs the app straight from the store can't be
              traced back to it.
            </p>
          </div>
          <CopyLink url={url} />
        </div>
      </AtlasPanel>

      <AtlasPanel title="Sign-ups so far">
        <div className="r-wrap" style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
          <StatBlock value={<span className="tnum">{signedUp}</span>} label="Signed up" size="md" />
          <StatBlock
            value={<span className="tnum">{sourceConnected}</span>}
            label="Connected a source"
            sub={signedUp > 0 ? `${signedUp - sourceConnected} still to connect` : null}
            size="md"
          />
          <StatBlock
            value={<span className="tnum">{activated}</span>}
            label="Synced a walk"
            sub={signedUp > 0 ? `${signedUp - activated} yet to sync` : null}
            size="md"
          />
        </div>
        <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "16px 0 0" }}>
          "Synced a walk" means their first walk has been through Atlas. Staff accounts are not counted, and nobody's
          details are shown here, only the totals.
        </p>
      </AtlasPanel>

      <AtlasPanel title="By week">
        {weeksWithActivity.length === 0 ? (
          <p style={{ fontSize: 14, color: "var(--text-muted)", margin: 0 }}>
            Nothing yet. Weeks appear here as people sign up.
          </p>
        ) : (
          <div className="r-scroll-x">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  <th className="eyebrow" style={thStyle}>Week</th>
                  <th className="eyebrow" style={{ ...thStyle, textAlign: "right" }}>Signed up</th>
                  <th className="eyebrow" style={{ ...thStyle, textAlign: "right" }}>Synced a walk</th>
                </tr>
              </thead>
              <tbody>
                {weeksWithActivity.map((w) => (
                  <tr key={w.weekStart} style={{ borderTop: "1px solid var(--border-subtle)" }}>
                    <td style={tdStyle}>{formatWeek(w.weekStart)}</td>
                    <td className="tnum" style={{ ...tdStyle, textAlign: "right" }}>{w.signedUp}</td>
                    <td className="tnum" style={{ ...tdStyle, textAlign: "right" }}>{w.activated}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "10px 0 0" }}>
              A walk counts in the week it synced, which can be later than the week they signed up.
            </p>
          </div>
        )}
      </AtlasPanel>
    </div>
  );
}

const thStyle: React.CSSProperties = { textAlign: "left", fontSize: 11, padding: "0 0 8px", fontWeight: 400 };
const tdStyle: React.CSSProperties = { padding: "10px 0", color: "var(--text-primary)" };

export default function PartnerPage() {
  usePageMeta({
    title: "Partner",
    description: "Sign-ups through your Atlas partner link.",
    canonicalPath: "/partner",
    noindex: true,
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: PARTNER_DASHBOARD_KEY,
    queryFn: fetchPartnerDashboard,
  });

  return (
    <div style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div className="r-page" style={{ padding: "28px 28px 20px" }}>
        <p className="eyebrow" style={{ fontSize: 11, marginBottom: 4 }}>Partner</p>
        <h1 style={{ font: "var(--type-h2)", color: "var(--text-primary)", margin: 0 }}>Your sign-ups</h1>
      </div>

      <div className="r-page-x" style={{ padding: "0 28px 28px", display: "flex", flexDirection: "column", gap: 28, maxWidth: 880 }}>
        {isLoading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Skel height="88px" />
            <Skel height="140px" />
          </div>
        )}
        {isError && (
          <p style={{ fontSize: 14, color: "var(--status-danger)" }}>
            Couldn't load your sign-ups. Try again in a moment.
          </p>
        )}
        {data && data.codes.length === 0 && (
          <EmptyState
            icon={<Link2 size={28} />}
            title="No partner link on this account"
            hint="If you're working with Atlas, ask us to link your partner code to this account."
          />
        )}
        {data?.codes.map((code) => (
          <div key={code.code}>
            {data.codes.length > 1 && (
              <h2 style={{ font: "var(--type-h3)", color: "var(--text-primary)", margin: "0 0 12px" }}>
                {code.label ?? code.code}
              </h2>
            )}
            <CodePanel code={code} />
          </div>
        ))}
      </div>
    </div>
  );
}
